import React, { useId } from 'react';
import { CaretDownFilled, CaretUpFilled } from '@ant-design/icons';
import { Flex, InputNumber, Select } from 'antd';
import styles from './RouteDeviation.module.css';
import { MILES_INVALID, toleranceHelper } from './routeDeviationCopy';

export const MILES_MIN = 1;
export const MILES_MAX = 10;
// The arrows move to the next half mile; a typed value may have one decimal.
export const MILES_STEP = 0.5;
const MILES_DECIMALS = 1;
export const DEFAULT_MINUTES_OFF = 10;

const MINUTE_PRESETS = [0, 5, 10, 15, 30, 60];
const MINUTE_OPTIONS = MINUTE_PRESETS.map((minutes) => ({
  value: minutes,
  label: minutes === 0 ? 'Immediately' : `${minutes} min`,
}));

const clampMiles = (miles: number): number =>
  Math.min(MILES_MAX, Math.max(MILES_MIN, miles));

const roundMiles = (miles: number): number =>
  Number(miles.toFixed(MILES_DECIMALS));

/** Whether miles is in the range and precision the server accepts. */
export const isValidMiles = (miles: number | null): miles is number =>
  miles !== null &&
  Number.isFinite(miles) &&
  miles >= MILES_MIN &&
  miles <= MILES_MAX &&
  roundMiles(miles) === miles;

/** The nearest allowed value: inside the range, to one decimal. */
export const snapMiles = (miles: number): number =>
  clampMiles(roundMiles(miles));

/**
 * Where an arrow press lands from `miles`: the next half mile in its
 * direction, so 3.2 goes up to 3.5 and down to 3.0.
 */
export const stepMiles = (
  miles: number,
  direction: 'up' | 'down',
  offset: number = MILES_STEP
): number => {
  const steps = miles / MILES_STEP;
  const base =
    direction === 'up'
      ? Math.floor(steps) * MILES_STEP
      : Math.ceil(steps) * MILES_STEP;
  return clampMiles(direction === 'up' ? base + offset : base - offset);
};

interface RouteDeviationToleranceFieldsProps {
  miles: number | null;
  minutes: number;
  onMilesChange: (miles: number | null) => void;
  onMinutesChange: (minutes: number) => void;
}

const RouteDeviationToleranceFields: React.FC<
  RouteDeviationToleranceFieldsProps
> = ({ miles, minutes, onMilesChange, onMinutesChange }) => {
  const helperId = useId();
  const valid = isValidMiles(miles);

  // The field clamps to the range itself, but only when the user leaves it, and
  // it does not report a typed number outside the range. Pass that number on,
  // so the helper and Save follow what is on screen while the user types.
  const handleInput = (text: string) => {
    const typed = text.trim() === '' ? NaN : Number(text);
    if (Number.isFinite(typed) && (typed < MILES_MIN || typed > MILES_MAX)) {
      onMilesChange(typed);
    }
  };

  // Leaving the field settles it inside the range, to one decimal; an empty
  // field stays empty so the helper can say what is missing.
  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const typed = Number(event.target.value);
    if (event.target.value.trim() === '' || !Number.isFinite(typed)) return;
    const snapped = snapMiles(typed);
    if (snapped !== miles) onMilesChange(snapped);
  };

  // The field adds its own step to the value (3.2 + 1 = 4.2) before this runs;
  // replace that with the next half mile.
  const handleStep = (
    _value: number | null,
    info: { offset: number | string; type: 'up' | 'down' }
  ) => {
    const from = Number.isFinite(miles) && miles !== null ? miles : MILES_MIN;
    // Shift+arrow moves the field ten steps; keep that as five miles.
    const offset = Number(info.offset) > 1 ? MILES_STEP * 10 : MILES_STEP;
    onMilesChange(stepMiles(from, info.type, offset));
  };

  return (
    <Flex vertical gap={8}>
      <span className={styles.sectionLabel}>Tolerance</span>
      <div className={styles.toleranceRow}>
        <InputNumber
          aria-label='Miles off route'
          aria-describedby={helperId}
          aria-invalid={!valid}
          className={styles.toleranceControl}
          variant='filled'
          controls={{
            upIcon: <CaretUpFilled />,
            downIcon: <CaretDownFilled />,
          }}
          min={MILES_MIN}
          max={MILES_MAX}
          // The field shows as many decimals as its step has; a whole step
          // keeps 3 as "3" and 3.2 as "3.2". handleStep sets the real step.
          step={1}
          value={miles}
          status={valid ? undefined : 'error'}
          onChange={onMilesChange}
          onInput={handleInput}
          onBlur={handleBlur}
          onStep={handleStep}
        />
        {/* Miles is the only unit, so the design's unit box is a label, not a choice. */}
        <span className={`${styles.toleranceControl} ${styles.toleranceUnit}`}>
          miles
        </span>
        <Select
          aria-label='Minutes off route'
          className={styles.toleranceControl}
          variant='filled'
          options={MINUTE_OPTIONS}
          value={minutes}
          onChange={onMinutesChange}
        />
      </div>
      <span
        id={helperId}
        className={
          valid ? styles.state : `${styles.state} ${styles.stateError}`
        }
      >
        {valid ? toleranceHelper(miles, minutes) : MILES_INVALID}
      </span>
    </Flex>
  );
};

export default RouteDeviationToleranceFields;
